import React, { useCallback, useMemo } from 'react';
import {
  TablePaginatedV8Core,
  CellLeftV8,
  CellCenterV8,
  CellDateV8,
  EyeColumnV8,
  order2Sorting,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { getRouteByName } from 'app/routes';
import {
  isActiveColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import {
  currentItemPrice,
  formatPrice,
  formatPriceHistory,
  itemUsageLabel,
  priceCalculationMethodLabel,
} from '../item-price';

/** Grid ειδών εισφοράς με την ισχύουσα τιμή και το ιστορικό τιμών στην ίδια γραμμή */
export const DataGrid = (props: any) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('CntItem'), { id: row.id }),
        state: {
          pathname: location.pathname,
          ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'code',
        header: t('Κωδικός'),
        columnName: t('Κωδικός'),
        accessorKey: 'code',
        orderByField: 'code',
        cell: CellCenterV8,
        size: 12,
      },
      {
        id: 'group.name',
        header: t('Ομάδα'),
        columnName: t('Ομάδα'),
        accessorFn: (row) => row.group?.name ?? '',
        orderByField: 'group.name',
        cell: CellLeftV8,
        size: 28,
      },
      {
        id: 'subgroup.name',
        header: t('Υποομάδα'),
        columnName: t('Υποομάδα'),
        accessorFn: (row) => row.subgroup?.name ?? '',
        orderByField: 'subgroup.name',
        cell: CellLeftV8,
        size: 28,
      },
      {
        id: 'name',
        header: t('Είδος'),
        columnName: t('Είδος'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: CellLeftV8,
        size: 40,
      },
      {
        id: 'enName',
        header: t('Είδος (EN)'),
        columnName: t('Είδος (EN)'),
        accessorKey: 'enName',
        orderByField: 'enName',
        cell: CellLeftV8,
        size: 40,
      },
      {
        id: 'units',
        header: t('Μονάδες'),
        columnName: t('Μονάδες'),
        accessorFn: (row) => [row.units1, row.units2, row.units3].filter(Boolean).join(' / '),
        cell: CellLeftV8,
        size: 22,
      },
      {
        id: 'priceCalculationMethod',
        header: t('Υπολογισμός τιμής'),
        columnName: t('Υπολογισμός τιμής'),
        accessorFn: (row) => priceCalculationMethodLabel(row.priceCalculationMethod, t),
        orderByField: 'priceCalculationMethod',
        cell: CellLeftV8,
        size: 24,
      },
      {
        id: 'currentPrice',
        header: t('Τρέχουσα τιμή'),
        columnName: t('Τρέχουσα τιμή'),
        accessorFn: (row) => formatPrice(currentItemPrice(row.itemPrices)?.price),
        cell: CellCenterV8,
        size: 16,
      },
      {
        id: 'currentPriceFrom',
        header: t('Ισχύς από'),
        columnName: t('Ισχύς από'),
        accessorFn: (row) => currentItemPrice(row.itemPrices)?.fromDate ?? null,
        cell: CellDateV8,
        size: 14,
      },
      {
        id: 'erpItemCode',
        header: t('Κωδικός ERP'),
        columnName: t('Κωδικός ERP'),
        accessorKey: 'erpItemCode',
        orderByField: 'erpItemCode',
        // Στη βάση το κενό αποθηκεύεται ως το κείμενο "null"
        cell: ({ getValue }) => {
          const value = getValue();
          const text = value == null || value === 'null' ? '' : String(value);
          return <div className="text-start">{text}</div>;
        },
        size: 16,
      },
      {
        id: 'erpItemName',
        header: t('Ονομασία ERP'),
        columnName: t('Ονομασία ERP'),
        accessorKey: 'erpItemName',
        orderByField: 'erpItemName',
        cell: CellLeftV8,
        size: 28,
      },
      {
        id: 'empaCategory',
        header: t('Κατηγορία ΕΜΠΑ'),
        columnName: t('Κατηγορία ΕΜΠΑ'),
        accessorKey: 'empaCategory',
        orderByField: 'empaCategory',
        cell: CellLeftV8,
        size: 18,
      },
      {
        id: 'enEmpaCategory',
        header: t('Κατηγορία ΕΜΠΑ (EN)'),
        columnName: t('Κατηγορία ΕΜΠΑ (EN)'),
        accessorKey: 'enEmpaCategory',
        orderByField: 'enEmpaCategory',
        cell: CellLeftV8,
        size: 18,
      },
      {
        id: 'usage',
        header: t('Χρήση'),
        columnName: t('Χρήση'),
        accessorFn: (row) => itemUsageLabel(row, t),
        cell: CellLeftV8,
        size: 28,
      },
      {
        id: 'hasDetails',
        header: t('Αναλυτικά'),
        columnName: t('Αναλυτικά'),
        accessorFn: (row) => (row.hasDetails ? t('ΝΑΙ') : t('ΟΧΙ')),
        orderByField: 'hasDetails',
        cell: CellCenterV8,
        size: 12,
      },
      isActiveColumn(t, i18n),
    ],
    [t, i18n, rowClicked],
  );

  return (
    <TablePaginatedV8Core
      loading={loading}
      id="cnt-items-grid"
      appStore={appStore}
      columns={columns as any}
      data={data ?? []}
      dataCount={dataCount ?? 0}
      topBorderRadius={false}
      onRowClick={({ row }) => setSelected(row.original)}
      rowProps={(row: any) => {
        let style = undefined;
        if (row.original?.id === selected?.id) style = gridRowSelectedStyle;
        if (row.original?.isActive === false) style = { ...style, ...gridInactiveRowStyle };
        return { style };
      }}
      onPageChange={onPageChange}
      initPageIndex={initPageIndex}
      initSorting={order2Sorting(initOrderBy)}
      onSortByChange={(_sort, _col, orderBy) => onOrderChange(orderBy)}
    />
  );
};
